import { deleteQueryKeys, readTrimmedParam, writeTrimmedParam } from "@/lib/filterUrlParams";
import type { DiaryEntryFilter } from "@/types/entities";

/** List table search box — synced separately from header {@link crmSyncStatus}. */
export const DIARY_SEARCH_PARAM_KEYS = ["search"] as const;

export const CLEARED_DIARY_SEARCH_FILTER: Partial<DiaryEntryFilter> = {
  search: undefined,
};

export function parseDiarySearchFromSearchParams(params: URLSearchParams): Pick<DiaryEntryFilter, "search"> {
  const search = readTrimmedParam(params, "search");
  return search ? { search } : {};
}

export function applyDiarySearchToSearchParams(
  filter: Pick<DiaryEntryFilter, "search">,
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  deleteQueryKeys(next, DIARY_SEARCH_PARAM_KEYS);
  writeTrimmedParam(next, "search", filter.search);
  return next;
}

function searchCanonical(filter: Pick<DiaryEntryFilter, "search">): string {
  return applyDiarySearchToSearchParams(filter, new URLSearchParams()).toString();
}

export function diarySearchFiltersEquivalent(
  a: Pick<DiaryEntryFilter, "search">,
  b: Pick<DiaryEntryFilter, "search">,
): boolean {
  return searchCanonical(a) === searchCanonical(b);
}

export const diarySearchFilterUrlSyncConfig = {
  parseFromSearchParams: parseDiarySearchFromSearchParams,
  applyToSearchParams: applyDiarySearchToSearchParams,
  filtersEquivalent: diarySearchFiltersEquivalent,
  clearedFilter: CLEARED_DIARY_SEARCH_FILTER,
};

/** Header sync-status dropdown (calendar + list). */
export const DIARY_CRM_SYNC_PARAM_KEYS = ["crmSyncStatus"] as const;

export const CLEARED_DIARY_CRM_SYNC_FILTER: Partial<DiaryEntryFilter> = {
  crmSyncStatus: undefined,
};

export function parseDiaryCrmSyncFromSearchParams(
  params: URLSearchParams,
): Pick<DiaryEntryFilter, "crmSyncStatus"> {
  const crmSyncStatus = readTrimmedParam(params, "crmSyncStatus");
  return crmSyncStatus ? { crmSyncStatus } : {};
}

export function applyDiaryCrmSyncToSearchParams(
  filter: Pick<DiaryEntryFilter, "crmSyncStatus">,
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  deleteQueryKeys(next, DIARY_CRM_SYNC_PARAM_KEYS);
  writeTrimmedParam(next, "crmSyncStatus", filter.crmSyncStatus);
  return next;
}

function crmSyncCanonical(filter: Pick<DiaryEntryFilter, "crmSyncStatus">): string {
  return applyDiaryCrmSyncToSearchParams(filter, new URLSearchParams()).toString();
}

export function diaryCrmSyncFiltersEquivalent(
  a: Pick<DiaryEntryFilter, "crmSyncStatus">,
  b: Pick<DiaryEntryFilter, "crmSyncStatus">,
): boolean {
  return crmSyncCanonical(a) === crmSyncCanonical(b);
}

export const diaryCrmSyncFilterUrlSyncConfig = {
  parseFromSearchParams: parseDiaryCrmSyncFromSearchParams,
  applyToSearchParams: applyDiaryCrmSyncToSearchParams,
  filtersEquivalent: diaryCrmSyncFiltersEquivalent,
  clearedFilter: CLEARED_DIARY_CRM_SYNC_FILTER,
};

/** Initial list/table filter from the landing URL. */
export function parseDiaryListFilterFromSearchParams(params: URLSearchParams): Partial<DiaryEntryFilter> {
  return {
    ...parseDiarySearchFromSearchParams(params),
    ...parseDiaryCrmSyncFromSearchParams(params),
  };
}
